import { Injectable } from '@angular/core';
import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';

/** Italian day-precision pickers: 31/12/2026. */
export const IT_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

/**
 * Month-precision pickers: 12/2026. Used by the billing-period selectors on
 * tickets, payments and costs, which address a whole month rather than a day.
 */
export const IT_MONTH_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: 'MM/YYYY' },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const pad = (n: number): string => n.toString().padStart(2, '0');

/**
 * Italian dd/MM/yyyy adapter.
 *
 * The app this replaces got Italian date parsing from @angular/material-moment-adapter.
 * Moment is gone, so the behaviour is reimplemented here on NativeDateAdapter —
 * without it, every datepicker silently falls back to US MM/DD/YYYY parsing.
 */
@Injectable({ providedIn: 'root' })
export class ItDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: unknown): string {
    if (isNaN(date.getTime())) {
      return '';
    }
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();

    return displayFormat === 'MM/YYYY' ? `${month}/${year}` : `${day}/${month}/${year}`;
  }

  /**
   * Returns null rather than undefined for unparseable input: Material treats null as
   * "empty" and undefined as "not handled", and the difference shows up as a control
   * that never reports itself invalid.
   */
  override parse(value: unknown): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value);
    }
    const parts = value.split('/').map((p) => Number(p));
    if (parts.some(isNaN)) {
      return null;
    }
    if (parts.length === 3) {
      return this.buildDate(parts[2], parts[1], parts[0]);
    }
    if (parts.length === 2) {
      return this.buildDate(parts[1], parts[0], 1);
    }
    return null;
  }

  private buildDate(year: number, month: number, day: number): Date | null {
    const date = new Date(year, month - 1, day);
    // Rejects overflow such as 31/02/2026, which the Date constructor rolls forward.
    const valid =
      date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    return valid ? date : null;
  }
}
